import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import questdbService from '../../services/questdb';
import { TextBoxElement, PipeElement, generatePipePath } from './scada';
import TagChartPopup from './scada/TagChartPopup';
import PumpFaceplate from './scada/PumpFaceplate';
import ValveFaceplate from './scada/ValveFaceplate';
import ControlValveFaceplate from './scada/ControlValveFaceplate';
import TagInspectorPopup from './scada/TagInspectorPopup';

// ── Inline arrow components (no separate file needed) ─────────────────────
const ArrowRight = ({ width = 60, height = 30, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 60 30"><line x1="4" y1="15" x2="46" y2="15" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="44,7 58,15 44,23" fill={startColor}/></svg>
);
const ArrowLeft = ({ width = 60, height = 30, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 60 30"><line x1="56" y1="15" x2="14" y2="15" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="16,7 2,15 16,23" fill={startColor}/></svg>
);
const ArrowUp = ({ width = 30, height = 60, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 30 60"><line x1="15" y1="56" x2="15" y2="14" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="7,16 15,2 23,16" fill={startColor}/></svg>
);
const ArrowDown = ({ width = 30, height = 60, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 30 60"><line x1="15" y1="4" x2="15" y2="46" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="7,44 15,58 23,44" fill={startColor}/></svg>
);
const ArrowBoth = ({ width = 60, height = 30, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 60 30"><line x1="14" y1="15" x2="46" y2="15" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="16,7 2,15 16,23" fill={startColor}/><polygon points="44,7 58,15 44,23" fill={startColor}/></svg>
);
// ──────────────────────────────────────────────────────────────────────────

// ── Pure color helpers at module level (never recreated on re-render) ──────────
const lightenColor = (hex, percent) => {
  const num = parseInt((hex || '#64748b').replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
  const B = Math.min(255, (num & 0x0000FF) + amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};
const darkenColor = (hex, percent) => {
  const num = parseInt((hex || '#64748b').replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
  const B = Math.max(0, (num & 0x0000FF) - amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

const PUMP_TYPES = new Set([
  'pump3D','centrifugalPump2','centrifugalPump4','classicPump1','coolPump',
  'horizontalPump3','horizontalPump4','horizontalSplitCasePump','magDriveNonMetallic',
  'magDrivePump2','meteringPump4','seallessPump','selfPrimingCentrifugalPump',
  'srhPump','verticalPump9','yellowPump'
]);

// SOV (Shut-Off Valves) — valve config + faceplate
const VALVE_TYPES = new Set([
  'handControlValve','valveWithActuator','ballValve1','blowdownValve','compactValve',
  'handValve1','handValve3','handValve4','pvcTwoWayBallValve'
]);

// Control Valves (PID) — control-valve config + faceplate
const CONTROL_VALVE_TYPES = new Set([
  'controlValveHandActuator','blueControlValve','controlValve3','controlValveDiaphragm',
  'controlValveGrayFitting','controlValue','valve3D','pressureBalancedDiaphragm',
  'safetyShutoffValve','regulatorExternalPressure','regulatorSelfContained',
  'rotaryPlugValve','valveLongShaft','valveFailLocked','handValveHorizontal'
]);

// React component imports (all with dynamic color support)
import BlowdownValveComponent from '../svg/3D-Blowdown-valve';
import PressureBalancedDiaphragm from '../svg/3-D Pressure-balanced-diaphragm-actuated';
import ClassicPump1 from '../svg/Classic_pump_1';
import RegulatorExternalPressure from '../svg/RegulatorExternalPressure';
import RegulatorSelfContained from '../svg/RegulatorSelfContained';
import RotaryPlugValve from '../svg/RotaryPlugValve';
import ValveLongShaft from '../svg/ValveLongShaft';
import ValveFailLocked from '../svg/ValveFailLocked';
import ControlValveHandActuator from '../svg/ControlValveHandActuator';
import HandControlValveIcon from '../svg/HandControlValveIcon';
import Pump3DIcon from '../svg/Pump3DIcon';
import Valve3DIcon from '../svg/Valve3DIcon';
import ValveWithActuatorIcon from '../svg/ValveWithActuatorIcon';
import BallValve1Icon from '../svg/BallValve1Icon';
import PVCTwoWayBallValveIcon from '../svg/PVCTwoWayBallValveIcon';
import BlueControlValveIcon from '../svg/BlueControlValveIcon';
import CompactValveIcon from '../svg/CompactValveIcon';
import ControlValve3Icon from '../svg/ControlValve3Icon';
import ControlValveDiaphragmIcon from '../svg/ControlValveDiaphragmIcon';
import ControlValveGrayFittingIcon from '../svg/ControlValveGrayFittingIcon';
import ControlValueIcon from '../svg/ControlValueIcon';
import SafetyShutoffValveIcon from '../svg/SafetyShutoffValveIcon';
import HandValveHorizontalIcon from '../svg/HandValveHorizontalIcon';
import HandValve1Icon from '../svg/HandValve1Icon';
import HandValve3Icon from '../svg/HandValve3Icon';
import HandValve4Icon from '../svg/HandValve4Icon';
import CentrifugalPump2Icon from '../svg/CentrifugalPump2Icon';
import CentrifugalPump4Icon from '../svg/CentrifugalPump4Icon';
import CoolPumpIcon from '../svg/CoolPumpIcon';
import HorizontalPump3Icon from '../svg/HorizontalPump3Icon';
import HorizontalPump4Icon from '../svg/HorizontalPump4Icon';
import HorizontalSplitCasePumpIcon from '../svg/HorizontalSplitCasePumpIcon';
import MagDriveNonMetallicIcon from '../svg/MagDriveNonMetallicIcon';
import MagDrivePump2Icon from '../svg/MagDrivePump2Icon';
import MeteringPump4Icon from '../svg/MeteringPump4Icon';
import SeallessPumpIcon from '../svg/SeallessPumpIcon';
import SelfPrimingCentrifugalPumpIcon from '../svg/SelfPrimingCentrifugalPumpIcon';
import SRHPumpIcon from '../svg/SRHPumpIcon';
import VerticalPump9Icon from '../svg/VerticalPump9Icon';
import YellowPumpIcon from '../svg/YellowPumpIcon';
import FlowTotalizerIcon from '../svg/FlowTotalizerIcon';
import Flowmeter2Icon from '../svg/Flowmeter2Icon';
import MagneticFlowmeter2Icon from '../svg/MagneticFlowmeter2Icon';
import MagneticFlowmeter3Icon from '../svg/MagneticFlowmeter3Icon';
import MassFlowmeterIcon from '../svg/MassFlowmeterIcon';
import TurbineMeter1Icon from '../svg/TurbineMeter1Icon';
import TurbineMeter3Icon from '../svg/TurbineMeter3Icon';
import FlamelessThermalOxidizerIcon from '../svg/FlamelessThermalOxidizerIcon';
import HeatExchanger1Icon from '../svg/HeatExchanger1Icon';
import HeatExchanger10Icon from '../svg/HeatExchanger10Icon';
import HeatExchanger3Icon from '../svg/HeatExchanger3Icon';
import HeatExchanger4Icon from '../svg/HeatExchanger4Icon';
import SimpleSteamGenerator2Icon from '../svg/SimpleSteamGenerator2Icon';
import ThermalFluidHeater1Icon from '../svg/ThermalFluidHeater1Icon';
import Mixer3DIcon from '../svg/Mixer3DIcon';
import ImpellerIcon from '../svg/ImpellerIcon';
import Mixer1Icon from '../svg/Mixer1Icon';
import Mixer2Icon from '../svg/Mixer2Icon';
import MixerBladeIcon from '../svg/MixerBladeIcon';
import PneumaticMixer1Icon from '../svg/PneumaticMixer1Icon';
import PneumaticMixer2Icon from '../svg/PneumaticMixer2Icon';
import ScrewTypeMixerIcon from '../svg/ScrewTypeMixerIcon';
import SimpleMixerIcon from '../svg/SimpleMixerIcon';
import SubmersibleMixerIcon from '../svg/SubmersibleMixerIcon';
import TankWithAgitatorIcon from '../svg/TankWithAgitatorIcon';
import TurbineAgitator1Icon from '../svg/TurbineAgitator1Icon';
import TurbineAgitator2Icon from '../svg/TurbineAgitator2Icon';
import TurbineAgitator3Icon from '../svg/TurbineAgitator3Icon';
import Boiler10Icon from '../svg/Boiler10Icon';
import Boiler5Icon from '../svg/Boiler5Icon';
import Boiler9Icon from '../svg/Boiler9Icon';
import BoilerWithFlamesPipeIcon from '../svg/BoilerWithFlamesPipeIcon';
import BoilerWithFlamesIcon from '../svg/BoilerWithFlamesIcon';
import GasFiredBoiler1Icon from '../svg/GasFiredBoiler1Icon';
import PackagedFiretubeBoilerIcon from '../svg/PackagedFiretubeBoilerIcon';
import CoolingTower1Icon from '../svg/CoolingTower1Icon';
import CoolingTower2Icon from '../svg/CoolingTower2Icon';
import CoolingTower3Icon from '../svg/CoolingTower3Icon';
import CoolingTower4Icon from '../svg/CoolingTower4Icon';
import CoolingUnitIcon from '../svg/CoolingUnitIcon';
import LargePortableChillerIcon from '../svg/LargePortableChillerIcon';
import TemperatureControlUnitIcon from '../svg/TemperatureControlUnitIcon';
import ChamberWithFlangeIcon from '../svg/ChamberWithFlangeIcon';
import HopperIcon from '../svg/HopperIcon';
import PolyethyleneTankIcon from '../svg/PolyethyleneTankIcon';
import Reactor6Icon from '../svg/Reactor6Icon';
import Reactor7Icon from '../svg/Reactor7Icon';
import ReactorTankIcon from '../svg/ReactorTankIcon';
import StabilizationTankIcon from '../svg/StabilizationTankIcon';
import StainlessSteelCylinderIcon from '../svg/StainlessSteelCylinderIcon';
import StorageFacility2Icon from '../svg/StorageFacility2Icon';
import StorageTankWithLadderIcon from '../svg/StorageTankWithLadderIcon';
import Tank11Icon from '../svg/Tank11Icon';
import Tank13Icon from '../svg/Tank13Icon';
import Tank15Icon from '../svg/Tank15Icon';
import Tank2Icon from '../svg/Tank2Icon';
import Tank21Icon from '../svg/Tank21Icon';
import Tank23Icon from '../svg/Tank23Icon';
import Tank27Icon from '../svg/Tank27Icon';
import Tank28Icon from '../svg/Tank28Icon';
import Tank37Icon from '../svg/Tank37Icon';
import Tank40Icon from '../svg/Tank40Icon';
import Tank9Icon from '../svg/Tank9Icon';
import TankWithHatchIcon from '../svg/TankWithHatchIcon';
import TankWithRivetsLadderIcon from '../svg/TankWithRivetsLadderIcon';
import Pipe90Curve1Icon from '../svg/Pipe90Curve1Icon';
import Pipe90Curve2Icon from '../svg/Pipe90Curve2Icon';
import Pipe90Curve3Icon from '../svg/Pipe90Curve3Icon';
import Pipe90Curve4Icon from '../svg/Pipe90Curve4Icon';
import PipeCutEnd1Icon from '../svg/PipeCutEnd1Icon';
import PipeCutEnd2Icon from '../svg/PipeCutEnd2Icon';
import PipeIntersection1Icon from '../svg/PipeIntersection1Icon';
import PipeLongHorizontalIcon from '../svg/PipeLongHorizontalIcon';
import PipeLongVerticalIcon from '../svg/PipeLongVerticalIcon';
import PipeOpenEnd1Icon from '../svg/PipeOpenEnd1Icon';
import PipeOpenEnd2Icon from '../svg/PipeOpenEnd2Icon';
import PipeRightAngle1Icon from '../svg/PipeRightAngle1Icon';
import PipeRightAngle2Icon from '../svg/PipeRightAngle2Icon';
import PipeRightAngle3Icon from '../svg/PipeRightAngle3Icon';
import PipeRightAngle4Icon from '../svg/PipeRightAngle4Icon';
import PipeShortHorizontalIcon from '../svg/PipeShortHorizontalIcon';
import PipeShortVerticalIcon from '../svg/PipeShortVerticalIcon';
import PipeTee1Icon from '../svg/PipeTee1Icon';
import PipeTee2Icon from '../svg/PipeTee2Icon';
import PipeTee3Icon from '../svg/PipeTee3Icon';
import PipeTee4Icon from '../svg/PipeTee4Icon';

const SVG_COMPONENTS = {
  // === PUMPS ===
  pump3D: { name: '3D Pump', width: 90, height: 80, component: Pump3DIcon, type: 'component', color: '#3b82f6', category: 'pump' },
  centrifugalPump2: { name: 'Centrifugal Pump 2', width: 90, height: 80, component: CentrifugalPump2Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  centrifugalPump4: { name: 'Centrifugal Pump 4', width: 80, height: 80, component: CentrifugalPump4Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  classicPump1: { name: 'Classic Pump', width: 70, height: 70, component: ClassicPump1, type: 'component', color: '#3b82f6', category: 'pump' },
  coolPump: { name: 'Cool Pump', width: 80, height: 80, component: CoolPumpIcon, type: 'component', color: '#06b6d4', category: 'pump' },
  horizontalPump3: { name: 'Horizontal Pump 3', width: 100, height: 70, component: HorizontalPump3Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  horizontalPump4: { name: 'Horizontal Pump 4', width: 100, height: 70, component: HorizontalPump4Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  horizontalSplitCasePump: { name: 'Split Case Pump', width: 100, height: 80, component: HorizontalSplitCasePumpIcon, type: 'component', color: '#3b82f6', category: 'pump' },
  magDriveNonMetallic: { name: 'Mag Drive (Non-Metal)', width: 80, height: 80, component: MagDriveNonMetallicIcon, type: 'component', color: '#06b6d4', category: 'pump' },
  magDrivePump2: { name: 'Mag Drive Pump 2', width: 90, height: 80, component: MagDrivePump2Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  meteringPump4: { name: 'Metering Pump', width: 80, height: 80, component: MeteringPump4Icon, type: 'component', color: '#8b5cf6', category: 'pump' },
  seallessPump: { name: 'Sealless Pump', width: 80, height: 80, component: SeallessPumpIcon, type: 'component', color: '#10b981', category: 'pump' },
  selfPrimingCentrifugalPump: { name: 'Self-Priming Pump', width: 90, height: 80, component: SelfPrimingCentrifugalPumpIcon, type: 'component', color: '#3b82f6', category: 'pump' },
  srhPump: { name: 'SRH Pump', width: 90, height: 90, component: SRHPumpIcon, type: 'component', color: '#3b82f6', category: 'pump' },
  verticalPump9: { name: 'Vertical Pump', width: 70, height: 100, component: VerticalPump9Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  yellowPump: { name: 'Yellow Pump', width: 80, height: 80, component: YellowPumpIcon, type: 'component', color: '#eab308', category: 'pump' },
  // === VALVES ===
  // === SOV (Shut-Off Valves) ===
  handControlValve: { name: 'Hand Control Valve', width: 60, height: 70, component: HandControlValveIcon, type: 'component', color: '#f59e0b', category: 'sov' },
  valveWithActuator: { name: 'Valve with Actuator', width: 70, height: 90, component: ValveWithActuatorIcon, type: 'component', color: '#10b981', category: 'sov' },
  ballValve1: { name: 'Ball Valve', width: 70, height: 70, component: BallValve1Icon, type: 'component', color: '#64748b', category: 'sov' },
  blowdownValve: { name: 'Blowdown Valve', width: 60, height: 80, component: BlowdownValveComponent, type: 'component', color: '#ef4444', category: 'sov' },
  compactValve: { name: 'Compact Valve', width: 60, height: 70, component: CompactValveIcon, type: 'component', color: '#06b6d4', category: 'sov' },
  handValve1: { name: 'Hand Valve 1', width: 60, height: 80, component: HandValve1Icon, type: 'component', color: '#f59e0b', category: 'sov' },
  handValve3: { name: 'Hand Valve 2', width: 70, height: 80, component: HandValve3Icon, type: 'component', color: '#f59e0b', category: 'sov' },
  handValve4: { name: 'Hand Valve 3', width: 70, height: 80, component: HandValve4Icon, type: 'component', color: '#f59e0b', category: 'sov' },
  pvcTwoWayBallValve: { name: 'PVC Two-Way Ball Valve', width: 60, height: 70, component: PVCTwoWayBallValveIcon, type: 'component', color: '#64748b', category: 'sov' },
  // === Control Valves (PID) ===
  controlValveHandActuator: { name: 'Control Valve (Hand)', width: 70, height: 80, component: ControlValveHandActuator, type: 'component', color: '#3b82f6', category: 'controlValve' },
  pressureBalancedDiaphragm: { name: 'Pressure Balanced Diaphragm', width: 70, height: 90, component: PressureBalancedDiaphragm, type: 'component', color: '#06b6d4', category: 'controlValve' },
  valve3D: { name: 'Valve Top', width: 60, height: 80, component: Valve3DIcon, type: 'component', color: '#f59e0b', category: 'controlValve' },
  controlValue: { name: 'Control Valve', width: 70, height: 80, component: ControlValueIcon, type: 'component', color: '#3b82f6', category: 'controlValve' },
  blueControlValve: { name: 'Blue Control Valve', width: 70, height: 90, component: BlueControlValveIcon, type: 'component', color: '#3b82f6', category: 'controlValve' },
  controlValve3: { name: 'Control Valve 3', width: 70, height: 80, component: ControlValve3Icon, type: 'component', color: '#3b82f6', category: 'controlValve' },
  controlValveDiaphragm: { name: 'Control Valve (Diaphragm)', width: 70, height: 90, component: ControlValveDiaphragmIcon, type: 'component', color: '#3b82f6', category: 'controlValve' },
  controlValveGrayFitting: { name: 'Control Valve (Gray)', width: 70, height: 90, component: ControlValveGrayFittingIcon, type: 'component', color: '#64748b', category: 'controlValve' },
  safetyShutoffValve: { name: 'Safety Shutoff Valve', width: 70, height: 90, component: SafetyShutoffValveIcon, type: 'component', color: '#ef4444', category: 'controlValve' },
  regulatorExternalPressure: { name: 'Regulator (External)', width: 80, height: 80, component: RegulatorExternalPressure, type: 'component', color: '#8b5cf6', category: 'controlValve' },
  regulatorSelfContained: { name: 'Regulator (Self-Contained)', width: 80, height: 80, component: RegulatorSelfContained, type: 'component', color: '#8b5cf6', category: 'controlValve' },
  rotaryPlugValve: { name: 'Rotary Plug Valve', width: 70, height: 70, component: RotaryPlugValve, type: 'component', color: '#f59e0b', category: 'controlValve' },
  valveLongShaft: { name: 'Valve Long Shaft', width: 60, height: 90, component: ValveLongShaft, type: 'component', color: '#f59e0b', category: 'controlValve' },
  valveFailLocked: { name: 'Valve Fail Locked', width: 70, height: 80, component: ValveFailLocked, type: 'component', color: '#ef4444', category: 'controlValve' },
  handValveHorizontal: { name: 'Hand Valve 1', width: 80, height: 60, component: HandValveHorizontalIcon, type: 'component', color: '#f59e0b', category: 'controlValve' },
  // === FLOW METERS ===
  flowTotalizer: { name: 'Flow Totalizer', width: 80, height: 80, component: FlowTotalizerIcon, type: 'component', color: '#10b981', category: 'flowmeter' },
  flowmeter2: { name: 'Flowmeter 2', width: 80, height: 80, component: Flowmeter2Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  magneticFlowmeter2: { name: 'Magnetic Flowmeter 2', width: 80, height: 80, component: MagneticFlowmeter2Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  magneticFlowmeter3: { name: 'Magnetic Flowmeter 3', width: 80, height: 80, component: MagneticFlowmeter3Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  massFlowmeter: { name: 'Mass Flowmeter', width: 80, height: 80, component: MassFlowmeterIcon, type: 'component', color: '#10b981', category: 'flowmeter' },
  turbineMeter1: { name: 'Turbine Meter 1', width: 80, height: 80, component: TurbineMeter1Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  turbineMeter3: { name: 'Turbine Meter 3', width: 80, height: 80, component: TurbineMeter3Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  // === HEATERS ===
  flamelessThermalOxidizer: { name: 'Flameless Oxidizer', width: 80, height: 80, component: FlamelessThermalOxidizerIcon, type: 'component', color: '#f59e0b', category: 'heater' },
  heatExchanger1: { name: 'Heat Exchanger 1', width: 80, height: 80, component: HeatExchanger1Icon, type: 'component', color: '#ef4444', category: 'heater' },
  heatExchanger10: { name: 'Heat Exchanger 10', width: 80, height: 80, component: HeatExchanger10Icon, type: 'component', color: '#ef4444', category: 'heater' },
  heatExchanger3: { name: 'Heat Exchanger 3', width: 80, height: 80, component: HeatExchanger3Icon, type: 'component', color: '#ef4444', category: 'heater' },
  heatExchanger4: { name: 'Heat Exchanger 4', width: 80, height: 80, component: HeatExchanger4Icon, type: 'component', color: '#ef4444', category: 'heater' },
  simpleSteamGenerator2: { name: 'Steam Generator 2', width: 80, height: 80, component: SimpleSteamGenerator2Icon, type: 'component', color: '#f59e0b', category: 'heater' },
  thermalFluidHeater1: { name: 'Thermal Fluid Heater', width: 80, height: 80, component: ThermalFluidHeater1Icon, type: 'component', color: '#ef4444', category: 'heater' },
  // === STIRRERS ===
  mixer3D: { name: '3D Mixer', width: 70, height: 90, component: Mixer3DIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  impeller: { name: 'Impeller', width: 70, height: 70, component: ImpellerIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  mixer1: { name: 'Mixer 1', width: 70, height: 90, component: Mixer1Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  mixer2: { name: 'Mixer 2', width: 70, height: 90, component: Mixer2Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  mixerBlade: { name: 'Mixer Blade', width: 70, height: 70, component: MixerBladeIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  pneumaticMixer1: { name: 'Pneumatic Mixer 1', width: 70, height: 90, component: PneumaticMixer1Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  pneumaticMixer2: { name: 'Pneumatic Mixer 2', width: 70, height: 90, component: PneumaticMixer2Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  screwTypeMixer: { name: 'Screw Mixer', width: 70, height: 90, component: ScrewTypeMixerIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  simpleMixer: { name: 'Simple Mixer', width: 70, height: 90, component: SimpleMixerIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  submersibleMixer: { name: 'Submersible Mixer', width: 70, height: 70, component: SubmersibleMixerIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  tankWithAgitator: { name: 'Tank w/ Agitator', width: 80, height: 90, component: TankWithAgitatorIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  turbineAgitator1: { name: 'Turbine Agitator 1', width: 70, height: 90, component: TurbineAgitator1Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  turbineAgitator2: { name: 'Turbine Agitator 2', width: 70, height: 90, component: TurbineAgitator2Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  turbineAgitator3: { name: 'Turbine Agitator 3', width: 70, height: 90, component: TurbineAgitator3Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  // === BOILERS ===
  boiler10: { name: 'Boiler 10', width: 80, height: 100, component: Boiler10Icon, type: 'component', color: '#ef4444', category: 'boiler' },
  boiler5: { name: 'Boiler 5', width: 80, height: 100, component: Boiler5Icon, type: 'component', color: '#ef4444', category: 'boiler' },
  boiler9: { name: 'Boiler 9', width: 80, height: 100, component: Boiler9Icon, type: 'component', color: '#ef4444', category: 'boiler' },
  boilerWithFlamesPipe: { name: 'Boiler w/ Pipe', width: 100, height: 100, component: BoilerWithFlamesPipeIcon, type: 'component', color: '#ef4444', category: 'boiler' },
  boilerWithFlames: { name: 'Boiler w/ Flames', width: 80, height: 100, component: BoilerWithFlamesIcon, type: 'component', color: '#ef4444', category: 'boiler' },
  gasFiredBoiler1: { name: 'Gas-Fired Boiler', width: 80, height: 100, component: GasFiredBoiler1Icon, type: 'component', color: '#ef4444', category: 'boiler' },
  packagedFiretubeBoiler: { name: 'Firetube Boiler', width: 100, height: 80, component: PackagedFiretubeBoilerIcon, type: 'component', color: '#ef4444', category: 'boiler' },
  // === COOLERS ===
  coolingTower1: { name: 'Cooling Tower 1', width: 80, height: 100, component: CoolingTower1Icon, type: 'component', color: '#06b6d4', category: 'cooler' },
  coolingTower2: { name: 'Cooling Tower 2', width: 80, height: 100, component: CoolingTower2Icon, type: 'component', color: '#06b6d4', category: 'cooler' },
  coolingTower3: { name: 'Cooling Tower 3', width: 80, height: 100, component: CoolingTower3Icon, type: 'component', color: '#06b6d4', category: 'cooler' },
  coolingTower4: { name: 'Cooling Tower 4', width: 80, height: 100, component: CoolingTower4Icon, type: 'component', color: '#06b6d4', category: 'cooler' },
  coolingUnit: { name: 'Cooling Unit', width: 80, height: 80, component: CoolingUnitIcon, type: 'component', color: '#06b6d4', category: 'cooler' },
  largePortableChiller: { name: 'Portable Chiller', width: 80, height: 80, component: LargePortableChillerIcon, type: 'component', color: '#06b6d4', category: 'cooler' },
  temperatureControlUnit: { name: 'Temp Control Unit', width: 80, height: 80, component: TemperatureControlUnitIcon, type: 'component', color: '#06b6d4', category: 'cooler' },
  // === TANKS ===
  chamberWithFlange: { name: 'Chamber w/ Flange', width: 80, height: 100, component: ChamberWithFlangeIcon, type: 'component', color: '#64748b', category: 'tank' },
  hopper: { name: 'Hopper', width: 80, height: 100, component: HopperIcon, type: 'component', color: '#64748b', category: 'tank' },
  polyethyleneTank: { name: 'Polyethylene Tank', width: 80, height: 100, component: PolyethyleneTankIcon, type: 'component', color: '#64748b', category: 'tank' },
  reactor6: { name: 'Reactor 6', width: 80, height: 100, component: Reactor6Icon, type: 'component', color: '#8b5cf6', category: 'tank' },
  reactor7: { name: 'Reactor 7', width: 80, height: 100, component: Reactor7Icon, type: 'component', color: '#8b5cf6', category: 'tank' },
  reactorTank: { name: 'Reactor Tank', width: 80, height: 100, component: ReactorTankIcon, type: 'component', color: '#8b5cf6', category: 'tank' },
  stabilizationTank: { name: 'Stabilization Tank', width: 80, height: 100, component: StabilizationTankIcon, type: 'component', color: '#64748b', category: 'tank' },
  stainlessSteelCylinder: { name: 'SS Cylinder', width: 70, height: 100, component: StainlessSteelCylinderIcon, type: 'component', color: '#94a3b8', category: 'tank' },
  storageFacility2: { name: 'Storage Facility 2', width: 100, height: 100, component: StorageFacility2Icon, type: 'component', color: '#64748b', category: 'tank' },
  storageTankWithLadder: { name: 'Tank w/ Ladder', width: 80, height: 100, component: StorageTankWithLadderIcon, type: 'component', color: '#64748b', category: 'tank' },
  tank11: { name: 'Tank 11', width: 80, height: 100, component: Tank11Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank13: { name: 'Tank 13', width: 80, height: 100, component: Tank13Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank15: { name: 'Tank 15', width: 80, height: 100, component: Tank15Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank2: { name: 'Tank 2', width: 80, height: 100, component: Tank2Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank21: { name: 'Tank 21', width: 80, height: 100, component: Tank21Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank23: { name: 'Tank 23', width: 80, height: 100, component: Tank23Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank27: { name: 'Tank 27', width: 80, height: 100, component: Tank27Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank28: { name: 'Tank 28', width: 80, height: 100, component: Tank28Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank37: { name: 'Tank 37', width: 80, height: 100, component: Tank37Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank40: { name: 'Tank 40', width: 80, height: 100, component: Tank40Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank9: { name: 'Tank 9', width: 80, height: 100, component: Tank9Icon, type: 'component', color: '#64748b', category: 'tank' },
  tankWithHatch: { name: 'Tank w/ Hatch', width: 80, height: 100, component: TankWithHatchIcon, type: 'component', color: '#64748b', category: 'tank' },
  tankWithRivetsLadder: { name: 'Tank Rivets+Ladder', width: 80, height: 100, component: TankWithRivetsLadderIcon, type: 'component', color: '#64748b', category: 'tank' },
  // === ARROWS ===
  arrowRight: { name: 'Arrow →', width: 60, height: 30, component: ArrowRight, type: 'component', color: '#ef4444', category: 'arrow' },
  arrowLeft:  { name: 'Arrow ←', width: 60, height: 30, component: ArrowLeft,  type: 'component', color: '#ef4444', category: 'arrow' },
  arrowUp:    { name: 'Arrow ↑', width: 30, height: 60, component: ArrowUp,    type: 'component', color: '#ef4444', category: 'arrow' },
  arrowDown:  { name: 'Arrow ↓', width: 30, height: 60, component: ArrowDown,  type: 'component', color: '#ef4444', category: 'arrow' },
  arrowBoth:  { name: 'Arrow ↔', width: 60, height: 30, component: ArrowBoth,  type: 'component', color: '#ef4444', category: 'arrow' },
  // === Pipe Fittings ===
  pipeLongHorizontal:  { name: 'Long horizontal pipe',  width: 80, height: 12, component: PipeLongHorizontalIcon,  type: 'component', color: '#64748b', category: 'pipe' },
  pipeLongVertical:    { name: 'Long vertical pipe',    width: 12, height: 80, component: PipeLongVerticalIcon,    type: 'component', color: '#64748b', category: 'pipe' },
  pipeIntersection1:   { name: 'Intersection 1',        width: 64, height: 64, component: PipeIntersection1Icon,   type: 'component', color: '#64748b', category: 'pipe' },
  pipeTee1:            { name: 'Tee 1',                 width: 64, height: 64, component: PipeTee1Icon,            type: 'component', color: '#64748b', category: 'pipe' },
  pipeTee2:            { name: 'Tee 2',                 width: 64, height: 64, component: PipeTee2Icon,            type: 'component', color: '#64748b', category: 'pipe' },
  pipeTee3:            { name: 'Tee 3',                 width: 64, height: 64, component: PipeTee3Icon,            type: 'component', color: '#64748b', category: 'pipe' },
  pipeTee4:            { name: 'Tee 4',                 width: 64, height: 64, component: PipeTee4Icon,            type: 'component', color: '#64748b', category: 'pipe' },
  pipe90Curve1:        { name: '90 curve 1',            width: 64, height: 64, component: Pipe90Curve1Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipe90Curve2:        { name: '90 curve 2',            width: 64, height: 64, component: Pipe90Curve2Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipe90Curve3:        { name: '90 curve 3',            width: 64, height: 64, component: Pipe90Curve3Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipe90Curve4:        { name: '90 curve 4',            width: 64, height: 64, component: Pipe90Curve4Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipeRightAngle1:     { name: 'Right angle 1',         width: 64, height: 64, component: PipeRightAngle1Icon,     type: 'component', color: '#64748b', category: 'pipe' },
  pipeRightAngle2:     { name: 'Right angle 2',         width: 64, height: 64, component: PipeRightAngle2Icon,     type: 'component', color: '#64748b', category: 'pipe' },
  pipeRightAngle3:     { name: 'Right angle 3',         width: 64, height: 64, component: PipeRightAngle3Icon,     type: 'component', color: '#64748b', category: 'pipe' },
  pipeRightAngle4:     { name: 'Right angle 4',         width: 64, height: 64, component: PipeRightAngle4Icon,     type: 'component', color: '#64748b', category: 'pipe' },
  pipeShortHorizontal: { name: 'Short horizontal pipe', width: 80, height: 24, component: PipeShortHorizontalIcon, type: 'component', color: '#64748b', category: 'pipe' },
  pipeShortVertical:   { name: 'Short vertical pipe',   width: 24, height: 80, component: PipeShortVerticalIcon,   type: 'component', color: '#64748b', category: 'pipe' },
  pipeOpenEnd1:        { name: 'Open end 1',            width: 40, height: 64, component: PipeOpenEnd1Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipeOpenEnd2:        { name: 'Open end 2',            width: 64, height: 40, component: PipeOpenEnd2Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipeCutEnd1:         { name: 'Cut end 1',             width: 48, height: 64, component: PipeCutEnd1Icon,         type: 'component', color: '#64748b', category: 'pipe' },
  pipeCutEnd2:         { name: 'Cut end 2',             width: 64, height: 48, component: PipeCutEnd2Icon,         type: 'component', color: '#64748b', category: 'pipe' }
};
// Memoized SVG icon wrapper — prevents SVG re-render (and Math.random gradient ID re-gen) when colors unchanged
const MemoizedIcon = React.memo(function MemoizedIcon({
  Component, width, height,
  startColor, midColor, endColor,
  stemStartColor, stemMidColor, stemEndColor,
  fillPercent, fillColor
}) {
  return (
    <Component
      width={width} height={height}
      startColor={startColor} midColor={midColor} endColor={endColor}
      stemStartColor={stemStartColor} stemMidColor={stemMidColor} stemEndColor={stemEndColor}
      fillPercent={fillPercent} fillColor={fillColor}
    />
  );
});

// ─── Shared faceplate/trend/inspector state across concurrent ScadaPanel instances ───
const _STATE = new Map(); // panelId → { fp, cp, ti }
const _SUBS  = new Map(); // panelId → Map<instanceId, fn>
const _pbroadcast = (pid, state, skip) => {
  _STATE.set(pid, state);
  _SUBS.get(pid)?.forEach((fn, iid) => { if (iid !== skip) fn(state); });
};
const _psubscribe = (pid, iid, fn) => {
  if (!_SUBS.has(pid)) _SUBS.set(pid, new Map());
  _SUBS.get(pid).set(iid, fn);
  return () => _SUBS.get(pid)?.delete(iid);
};
const _pclear = (pid) => {
  _STATE.delete(pid);
  _SUBS.get(pid)?.forEach(fn => fn({ fp: [], cp: [], ti: [] }));
};
const _getState = (pid) => _STATE.get(pid) || { fp: [], cp: [], ti: [] };
// ─────────────────────────────────────────────────────────────────────────────

function ScadaPanel({ config, darkMode, plantId, onNavigate, isActive = true }) {
  const panelId = config?.id;
  // Stable per-instance ID so we don't broadcast back to ourselves
  const _iid = useRef(String(Math.random())).current;

  const [tagValues, setTagValues] = useState({});
  // Initialize from shared store so a newly-active instance picks up state from its peer
  const [openFaceplates, _setFP] = useState(() => _getState(panelId).fp);
  const [chartPopups,    _setCP] = useState(() => _getState(panelId).cp);
  const [tagInspectors,  _setTI] = useState(() => _getState(panelId).ti);
  const containerRef = useRef(null);

  // Ref to current combined state — avoids stale closures in broadcasted setters
  const stateRef = useRef({ fp: openFaceplates, cp: chartPopups, ti: tagInspectors });
  useEffect(() => { stateRef.current.fp = openFaceplates; }, [openFaceplates]);
  useEffect(() => { stateRef.current.cp = chartPopups; }, [chartPopups]);
  useEffect(() => { stateRef.current.ti = tagInspectors; }, [tagInspectors]);

  // Wrapped setters that broadcast to peer instances (e.g. normal view while in fullscreen)
  const setOpenFaceplates = (updater) => {
    _setFP(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (panelId) _pbroadcast(panelId, { ...stateRef.current, fp: next }, _iid);
      return next;
    });
  };
  const setChartPopups = (updater) => {
    _setCP(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (panelId) _pbroadcast(panelId, { ...stateRef.current, cp: next }, _iid);
      return next;
    });
  };
  const setTagInspectors = (updater) => {
    _setTI(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (panelId) _pbroadcast(panelId, { ...stateRef.current, ti: next }, _iid);
      return next;
    });
  };

  // Subscribe to peer updates
  useEffect(() => {
    if (!panelId) return;
    return _psubscribe(panelId, _iid, (s) => { _setFP(s.fp || []); _setCP(s.cp || []); _setTI(s.ti || []); });
  }, [panelId]);

  // When panelId changes (dashboard switch), clear the old panel's shared state and reset
  const prevPanelId = useRef(panelId);
  useEffect(() => {
    if (prevPanelId.current !== panelId) {
      if (prevPanelId.current) _pclear(prevPanelId.current);
      prevPanelId.current = panelId;
      const s = _getState(panelId);
      _setFP(s.fp); _setCP(s.cp); _setTI(s.ti);
    }
  }, [panelId]);

  // ✅ Normalize config — memoized so reference is stable between renders
  const normalizedConfig = useMemo(() => ({
    ...config,
    components: config?.scadaElements || config?.components || [],
    lines: config?.scadaConnections || config?.lines || [],
    scadaConfig: config?.scadaConfig || {},
    customComponents: config?.customComponents || []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [config]);

  // Pre-compute tag list — only rebuilds when components/lines change
  const tagNames = useMemo(() => [...new Set([
    ...(normalizedConfig.components || []).flatMap(c => {
      const tags = [];
      if (c.tagName) tags.push(c.tagName);
      if (c.conditions) c.conditions.forEach(cond => cond.rules?.forEach(r => { if (r.tagName) tags.push(r.tagName); }));
      if (c.pumpConfig) {
        const p = c.pumpConfig;
        [p.onFeedback?.tagName, p.offFeedback?.tagName, p.trip?.tagName,
         p.onCommand?.tagName, p.offCommand?.tagName, p.mode?.tagName, p.remoteLocal?.tagName
        ].forEach(t => { if (t) tags.push(t); });
      }
      if (c.valveConfig) {
        const v = c.valveConfig;
        [v.onFeedback?.tagName, v.offFeedback?.tagName, v.trip?.tagName,
         v.onCommand?.tagName, v.offCommand?.tagName, v.mode?.tagName, v.remoteLocal?.tagName
        ].forEach(t => { if (t) tags.push(t); });
      }
      if (c.controlValveConfig) {
        const cv = c.controlValveConfig;
        [cv.pv?.tagName, cv.sp?.tagName, cv.op?.tagName, cv.mode?.tagName
        ].forEach(t => { if (t) tags.push(t); });
      }
      if (c.ledConfig) {
        [c.ledConfig.onFeedback?.tagName, c.ledConfig.offFeedback?.tagName
        ].forEach(t => { if (t) tags.push(t); });
      }
      return tags;
    }),
    ...(normalizedConfig.lines?.filter(l => l.tagName).map(l => l.tagName) || [])
  ])], [normalizedConfig.components, normalizedConfig.lines]);

  const theme = darkMode ? {
    bg: '#1a1d29',
    text: '#e5e7eb',
    textSecondary: '#9ca3af',
    border: '#374151'
  } : {
    bg: '#ffffff',
    text: '#111827',
    textSecondary: '#6b7280',
    border: '#e5e7eb'
  };

  // Fetch real-time tag values from scada_wide table
  useEffect(() => {
    const fetchTagValues = async () => {
      try {
        if (tagNames.length === 0) return;

        let result;
        try {
          const columnsToSelect = ['timestamp', ...tagNames].join(', ');
          const sql = `SELECT ${columnsToSelect} FROM scada_wide ORDER BY timestamp DESC LIMIT 1`;
          result = await questdbService.query(sql, plantId);
        } catch (colError) {
          // Fallback: some configured tag columns may not exist yet — fetch all columns
          const sql = `SELECT * FROM scada_wide ORDER BY timestamp DESC LIMIT 1`;
          result = await questdbService.query(sql, plantId);
        }

        if (result.length > 0) {
          const latestRow = result[0];
          const valueMap = {};
          tagNames.forEach(tagName => {
            if (latestRow[tagName] !== undefined && latestRow[tagName] !== null) {
              valueMap[tagName] = String(latestRow[tagName]);
            }
          });
          // Only trigger re-render if values actually changed
          setTagValues(prev => {
            const hasChanged = tagNames.some(t => prev[t] !== valueMap[t]);
            return hasChanged ? valueMap : prev;
          });
        }
      } catch (error) {
        console.error('Error fetching tag values:', error);
      }
    };

    fetchTagValues();
    // refreshInterval is set per-panel (PanelConfigModal). Fallback 2000 ms.
    // Minimum 500 ms guard so a typo can't melt the agent.
    const pollMs = Math.max(500, Number(config?.refreshInterval) || 2000);
    const interval = setInterval(fetchTagValues, pollMs);
    return () => clearInterval(interval);
  }, [tagNames, plantId, config?.refreshInterval]);

  // Evaluate a single rule against current tag values
  const evaluateRule = (rule) => {
    const tagVal = tagValues[rule.tagName];
    if (tagVal === undefined || tagVal === null) return false;
    const tv = String(tagVal);
    const rv = String(rule.value);
    switch (rule.operator) {
      case '==': return tv === rv;
      case '!=': return tv !== rv;
      case '>': return parseFloat(tv) > parseFloat(rv);
      case '<': return parseFloat(tv) < parseFloat(rv);
      case '>=': return parseFloat(tv) >= parseFloat(rv);
      case '<=': return parseFloat(tv) <= parseFloat(rv);
      case 'contains': return tv.toLowerCase().includes(rv.toLowerCase());
      default: return tv === rv;
    }
  };

  // Evaluate a condition (group of rules with AND/OR logic)
  const evaluateCondition = (cond) => {
    if (!cond.rules || cond.rules.length === 0) return false;
    const validRules = cond.rules.filter(r => r.tagName);
    if (validRules.length === 0) return false;
    if (cond.logic === 'OR') {
      return validRules.some(evaluateRule);
    }
    return validRules.every(evaluateRule);
  };

  // Find the best matching condition (most specific first)
  const getMatchingCondition = (comp) => {
    // New conditions format - prioritize conditions with more rules
    if (comp.conditions?.length) {
      const sorted = [...comp.conditions].sort((a, b) => (b.rules?.length || 0) - (a.rules?.length || 0));
      return sorted.find(evaluateCondition) || null;
    }
    // Backward compat: old colorMappings format
    if (comp.tagName && comp.colorMappings?.length) {
      const tagValue = tagValues[comp.tagName];
      if (tagValue === undefined) return null;
      const mapping = comp.colorMappings.find(m => String(m.value) === String(tagValue));
      return mapping ? { color: mapping.color, label: mapping.label } : null;
    }
    return null;
  };

  // Get color for a component based on conditions
  const getControlValveColor = (comp) => {
    const cfg = comp.controlValveConfig;
    if (!cfg) return comp.defaultColor || '#64748b';
    const opTag = cfg.op?.tagName;
    if (!opTag) return comp.defaultColor || '#64748b';
    const v = tagValues[opTag];
    const opVal = v !== undefined && v !== null ? parseFloat(v) : null;
    if (opVal === null) return comp.defaultColor || '#64748b';
    return opVal >= 1 ? (cfg.openColor || '#00ff00') : (cfg.closeColor || '#ff0000');
  };

  const getControlValveStatus = (comp) => {
    const cfg = comp.controlValveConfig;
    if (!cfg?.op?.tagName) return null;
    const v = tagValues[cfg.op.tagName];
    const opVal = v !== undefined && v !== null ? parseFloat(v) : null;
    if (opVal === null) return null;
    return opVal >= 1 ? 'OPEN' : 'CLOSED';
  };

  const getLedColorAndStatus = (comp) => {
    const cfg = comp.ledConfig;
    if (!cfg) return null;
    const onTag = cfg.onFeedback?.tagName;
    const offTag = cfg.offFeedback?.tagName;
    if (onTag) {
      const val = tagValues[onTag];
      if (val !== undefined) {
        if (parseFloat(val) >= 1) return { color: cfg.onFeedback.color || '#00ff00', label: cfg.onFeedback.label || 'ON' };
        return { color: cfg.offFeedback?.color || '#ff0000', label: cfg.offFeedback?.label || 'OFF' };
      }
    }
    if (offTag) {
      const val = tagValues[offTag];
      if (val !== undefined) {
        if (parseFloat(val) >= 1) return { color: cfg.offFeedback.color || '#ff0000', label: cfg.offFeedback.label || 'OFF' };
        return { color: cfg.onFeedback?.color || '#00ff00', label: cfg.onFeedback?.label || 'ON' };
      }
    }
    return null;
  };

  const getComponentColor = (comp) => {
    if (PUMP_TYPES.has(comp.type) && comp.pumpConfig) return getPumpColor(comp);
    if (VALVE_TYPES.has(comp.type) && comp.valveConfig) return getValveBodyColor(comp);
    if (CONTROL_VALVE_TYPES.has(comp.type) && comp.controlValveConfig) return getControlValveColor(comp);
    if (comp.type === 'led' && comp.ledConfig) {
      const led = getLedColorAndStatus(comp);
      return led ? led.color : (comp.defaultColor || '#64748b');
    }
    const match = getMatchingCondition(comp);
    return match ? match.color : (comp.defaultColor || '#64748b');
  };

  // Get status label for a component
  const getComponentStatus = (comp) => {
    if (PUMP_TYPES.has(comp.type) && comp.pumpConfig) return getPumpStatus(comp);
    if (VALVE_TYPES.has(comp.type) && comp.valveConfig) return getValveStatus(comp);
    if (CONTROL_VALVE_TYPES.has(comp.type) && comp.controlValveConfig) return getControlValveStatus(comp);
    if (comp.type === 'led' && comp.ledConfig) {
      const led = getLedColorAndStatus(comp);
      return led?.label || null;
    }
    const match = getMatchingCondition(comp);
    return match?.label || null;
  };

  // Pump-specific color evaluation
  const getPumpColor = (comp) => {
    const cfg = comp.pumpConfig;
    if (!cfg) return comp.defaultColor || '#64748b';

    const num = (tagName) => {
      const v = tagValues[tagName];
      return v !== undefined && v !== null ? parseFloat(v) : null;
    };

    const onVal  = cfg.onFeedback?.tagName  ? num(cfg.onFeedback.tagName)  : null;
    const offVal = cfg.offFeedback?.tagName ? num(cfg.offFeedback.tagName) : null;
    const tripVal = cfg.trip?.tagName       ? num(cfg.trip.tagName)        : null;

    // 1. Trip (highest priority)
    if (cfg.trip?.tagName && tripVal !== null && tripVal >= 1) return cfg.trip.color;

    // 2. Fault — both on_fb AND off_fb == 1 (only if both tags assigned)
    if (cfg.onFeedback?.tagName && cfg.offFeedback?.tagName && onVal !== null && offVal !== null) {
      if (onVal >= 1 && offVal >= 1) return cfg.faultColor || '#f59e0b';
      // 3. Intermediate — both == 0
      if (onVal === 0 && offVal === 0) return cfg.intermediateColor || '#fb923c';
    }

    // 4. On Feedback
    if (cfg.onFeedback?.tagName && onVal !== null && onVal >= 1) return cfg.onFeedback.color;

    // 5. Off Feedback (default)
    return cfg.offFeedback?.color || comp.defaultColor || '#64748b';
  };

  const getPumpStatus = (comp) => {
    const cfg = comp.pumpConfig;
    if (!cfg) return null;

    const num = (tagName) => {
      const v = tagValues[tagName];
      return v !== undefined && v !== null ? parseFloat(v) : null;
    };

    const onVal  = cfg.onFeedback?.tagName  ? num(cfg.onFeedback.tagName)  : null;
    const offVal = cfg.offFeedback?.tagName ? num(cfg.offFeedback.tagName) : null;
    const tripVal = cfg.trip?.tagName       ? num(cfg.trip.tagName)        : null;

    if (cfg.trip?.tagName && tripVal !== null && tripVal >= 1) return cfg.trip.label || 'Trip';
    if (cfg.onFeedback?.tagName && cfg.offFeedback?.tagName && onVal !== null && offVal !== null) {
      if (onVal >= 1 && offVal >= 1) return 'Fault';
      if (onVal === 0 && offVal === 0) return 'Intermediate';
    }
    if (cfg.onFeedback?.tagName && onVal !== null && onVal >= 1) return cfg.onFeedback.label || 'Running';
    return cfg.offFeedback?.label || 'Stopped';
  };

  // Valve-specific body color (feedback-based, same logic as pump)
  const getValveBodyColor = (comp) => {
    const cfg = comp.valveConfig;
    if (!cfg) return comp.defaultColor || '#64748b';

    const num = (tagName) => {
      const v = tagValues[tagName];
      return v !== undefined && v !== null ? parseFloat(v) : null;
    };

    const onVal  = cfg.onFeedback?.tagName  ? num(cfg.onFeedback.tagName)  : null;
    const offVal = cfg.offFeedback?.tagName ? num(cfg.offFeedback.tagName) : null;
    const tripVal = cfg.trip?.tagName       ? num(cfg.trip.tagName)        : null;

    if (cfg.trip?.tagName && tripVal !== null && tripVal >= 1) return cfg.trip.color;
    if (cfg.onFeedback?.tagName && cfg.offFeedback?.tagName && onVal !== null && offVal !== null) {
      if (onVal >= 1 && offVal >= 1) return cfg.faultColor || '#f59e0b';
      if (onVal === 0 && offVal === 0) return cfg.intermediateColor || '#fb923c';
    }
    if (cfg.onFeedback?.tagName && onVal !== null && onVal >= 1) return cfg.onFeedback.color;
    return cfg.offFeedback?.color || comp.defaultColor || '#64748b';
  };

  const getValveStatus = (comp) => {
    const cfg = comp.valveConfig;
    if (!cfg) return null;

    const num = (tagName) => {
      const v = tagValues[tagName];
      return v !== undefined && v !== null ? parseFloat(v) : null;
    };

    const onVal  = cfg.onFeedback?.tagName  ? num(cfg.onFeedback.tagName)  : null;
    const offVal = cfg.offFeedback?.tagName ? num(cfg.offFeedback.tagName) : null;
    const tripVal = cfg.trip?.tagName       ? num(cfg.trip.tagName)        : null;

    if (cfg.trip?.tagName && tripVal !== null && tripVal >= 1) return cfg.trip.label || 'Trip';
    if (cfg.onFeedback?.tagName && cfg.offFeedback?.tagName && onVal !== null && offVal !== null) {
      if (onVal >= 1 && offVal >= 1) return 'Fault';
      if (onVal === 0 && offVal === 0) return 'Intermediate';
    }
    if (cfg.onFeedback?.tagName && onVal !== null && onVal >= 1) return cfg.onFeedback.label || 'Open';
    return cfg.offFeedback?.label || 'Closed';
  };

  // Valve T-portion (stem) color — command-based; falls back to feedback if no command tags
  const getValveStemColor = (comp) => {
    const cfg = comp.valveConfig;
    if (!cfg) return comp.defaultColor || '#64748b';

    const num = (tagName) => {
      const v = tagValues[tagName];
      return v !== undefined && v !== null ? parseFloat(v) : null;
    };

    // If command tags are assigned, use command logic
    if (cfg.onCommand?.tagName || cfg.offCommand?.tagName) {
      const onCV  = cfg.onCommand?.tagName  ? num(cfg.onCommand.tagName)  : null;
      const offCV = cfg.offCommand?.tagName ? num(cfg.offCommand.tagName) : null;

      if (cfg.onCommand?.tagName && cfg.offCommand?.tagName && onCV !== null && offCV !== null) {
        if (onCV >= 1 && offCV >= 1) return cfg.faultColor || '#f59e0b';
        if (onCV === 0 && offCV === 0) return cfg.intermediateColor || '#fb923c';
      }
      if (cfg.onCommand?.tagName && onCV !== null && onCV >= 1) return cfg.onCommand.color || '#3b82f6';
      return cfg.offCommand?.color || comp.defaultColor || '#64748b';
    }

    // No command tags → fall back to feedback color (same as body)
    return getValveBodyColor(comp);
  };

  // Pre-compute display data for ALL components once per tagValues/component change
  // This avoids repeating color lookups on every render inside the map
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const componentDataMap = useMemo(() => {
    const map = new Map();
    normalizedConfig.components?.forEach(comp => {
      const bodyColor = getComponentColor(comp);
      const isValveType = VALVE_TYPES.has(comp.type);
      const stemColor = (isValveType && comp.valveConfig) ? getValveStemColor(comp) : bodyColor;
      const status = getComponentStatus(comp);
      map.set(comp.id, {
        bodyColor,
        stemColor,
        status,
        midColor: lightenColor(bodyColor, 60),
        endColorDark: darkenColor(bodyColor, 20),
        stemMidColor: lightenColor(stemColor, 60),
        stemEndColor: darkenColor(stemColor, 20),
      });
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagValues, normalizedConfig.components]);

  // Helper to get component dimensions
  const getComponentDimensions = (comp) => {
    if (comp.type === 'navButton') {
      return { width: comp.width || 120, height: comp.height || 40 };
    }
    if (comp.type === 'textbox') {
      return { width: comp.width || 120, height: comp.height || 40 };
    }
    if (comp.type === 'customImage') {
      return { width: comp.width || 64, height: comp.height || 64 };
    }
    if (comp.type === 'led') {
      const s = comp.width || 40;
      return { width: s, height: s };
    }
    if (comp.type === 'drawLine') {
      return { width: comp.width || 310, height: comp.height || 10 };
    }
    if (comp.type === 'shape') {
      return { width: comp.width || 100, height: comp.height || 100 };
    }
    const svgData = SVG_COMPONENTS[comp.type];
    return {
      width: comp.width || svgData?.width || 60,
      height: comp.height || svgData?.height || 60
    };
  };

  // Extract all tags from a component or pipe
  const getItemTags = (item) => {
    const tags = [];
    // For textboxes only include the tag when it's actively displayed (showTagValue=true)
    if (item.tagName && (item.type !== 'textbox' || item.showTagValue)) tags.push(item.tagName);
    if (item.conditions) item.conditions.forEach(cond => cond.rules?.forEach(r => { if (r.tagName) tags.push(r.tagName); }));
    return [...new Set(tags)];
  };

  // Collect all tags associated with a component
  const getCompTags = (comp) => {
    const tags = [];
    if (comp.tagName) tags.push(comp.tagName);
    if (comp.conditions) comp.conditions.forEach(cond => cond.rules?.forEach(r => { if (r.tagName) tags.push(r.tagName); }));
    if (comp.pumpConfig) {
      const p = comp.pumpConfig;
      [p.onFeedback?.tagName, p.offFeedback?.tagName, p.trip?.tagName,
       p.onCommand?.tagName, p.offCommand?.tagName, p.mode?.tagName, p.remoteLocal?.tagName
      ].forEach(t => { if (t) tags.push(t); });
    }
    if (comp.valveConfig) {
      const v = comp.valveConfig;
      [v.onFeedback?.tagName, v.offFeedback?.tagName, v.trip?.tagName,
       v.onCommand?.tagName, v.offCommand?.tagName, v.mode?.tagName, v.remoteLocal?.tagName
      ].forEach(t => { if (t) tags.push(t); });
    }
    if (comp.controlValveConfig) {
      const cv = comp.controlValveConfig;
      [cv.pv?.tagName, cv.sp?.tagName, cv.op?.tagName, cv.mode?.tagName
      ].forEach(t => { if (t) tags.push(t); });
    }
    return [...new Set(tags)];
  };

  // Double-click → Tag Inspector popup (toggle) — skip for nav buttons
  const handleDoubleClick = (e, comp) => {
    e.stopPropagation();
    if (comp.type === 'navButton') return;
    const allTags = getCompTags(comp);
    const w = 390, h = 440;
    let x = e.clientX + 16, y = e.clientY - 40;
    if (x + w > window.innerWidth  - 10) x = e.clientX - w - 16;
    if (y + h > window.innerHeight - 10) y = window.innerHeight - h - 10;
    if (y < 10) y = 10;
    if (x < 10) x = 10;
    setTagInspectors(prev => {
      const existing = prev.find(ti => ti.compId === comp.id);
      if (existing) return prev.filter(ti => ti.compId !== comp.id);
      return [...prev, { id: Date.now(), compId: comp.id, comp, allTags, position: { x, y } }];
    });
  };

  // Open / close pump faceplate on single click
  const handlePumpClick = (e, comp) => {
    e.stopPropagation();
    if (!comp.showFaceplate) return;
    // Position faceplate just to the right of the click, clamped to viewport
    const fpW = 310;
    const fpH = 200;
    let fx = e.clientX + 16;
    let fy = e.clientY - 40;
    if (fx + fpW > window.innerWidth - 10) fx = e.clientX - fpW - 16;
    if (fy + fpH > window.innerHeight - 10) fy = window.innerHeight - fpH - 10;
    if (fy < 10) fy = 10;
    if (fx < 10) fx = 10;
    setOpenFaceplates(prev => {
      const already = prev.find(f => f.id === comp.id);
      if (already) return prev.filter(f => f.id !== comp.id); // toggle off
      return [...prev, { ...comp, _initPos: { x: fx, y: fy } }];
    });
  };

  // Open / close valve faceplate on single click
  const handleValveClick = (e, comp) => {
    e.stopPropagation();
    if (!comp.showFaceplate) return;
    const fpW = 310;
    const fpH = 200;
    let fx = e.clientX + 16;
    let fy = e.clientY - 40;
    if (fx + fpW > window.innerWidth - 10) fx = e.clientX - fpW - 16;
    if (fy + fpH > window.innerHeight - 10) fy = window.innerHeight - fpH - 10;
    if (fy < 10) fy = 10;
    if (fx < 10) fx = 10;
    setOpenFaceplates(prev => {
      const already = prev.find(f => f.id === comp.id);
      if (already) return prev.filter(f => f.id !== comp.id);
      return [...prev, { ...comp, _initPos: { x: fx, y: fy } }];
    });
  };

  // Open / close control valve faceplate on single click
  const handleControlValveClick = (e, comp) => {
    e.stopPropagation();
    if (!comp.showFaceplate) return;
    const fpW = 320;
    const fpH = 250;
    let fx = e.clientX + 16;
    let fy = e.clientY - 40;
    if (fx + fpW > window.innerWidth - 10) fx = e.clientX - fpW - 16;
    if (fy + fpH > window.innerHeight - 10) fy = window.innerHeight - fpH - 10;
    if (fy < 10) fy = 10;
    if (fx < 10) fx = 10;
    setOpenFaceplates(prev => {
      const already = prev.find(f => f.id === comp.id);
      if (already) return prev.filter(f => f.id !== comp.id);
      return [...prev, { ...comp, _initPos: { x: fx, y: fy } }];
    });
  };

  // Open chart popup — uses fixed viewport coords to escape overflow:hidden parents
  const handleComponentClick = (e, item, label) => {
    if (!item.displayTrend) return;
    const tags = getItemTags(item);
    if (tags.length === 0) return;
    e.stopPropagation();
    const popupW = 390;
    const popupH = 300;
    let px = e.clientX + 14;
    let py = e.clientY - 40;
    if (px + popupW > window.innerWidth - 10) px = e.clientX - popupW - 14;
    if (py + popupH > window.innerHeight - 10) py = window.innerHeight - popupH - 10;
    if (py < 10) py = 10;
    if (px < 10) px = 10;
    setChartPopups(prev => [...prev, { id: Date.now(), tags, position: { x: px, y: py }, label: label || item.label || 'Chart' }]);
  };

  const getLineCoordinates = (line) => {
    // New format: free pipe with absolute coordinates
    if (line.startX !== undefined) {
      return { x1: line.startX, y1: line.startY, x2: line.endX, y2: line.endY };
    }
    // Legacy format: component-to-component
    const fromComp = normalizedConfig.components?.find(c => c.id === line.from);
    const toComp = normalizedConfig.components?.find(c => c.id === line.to);

    if (!fromComp || !toComp) return null;

    const fromDim = getComponentDimensions(fromComp);
    const toDim = getComponentDimensions(toComp);

    return {
      x1: fromComp.x + fromDim.width / 2,
      y1: fromComp.y + fromDim.height / 2,
      x2: toComp.x + toDim.width / 2,
      y2: toComp.y + toDim.height / 2
    };
  };

  // viewBox — always uses the canonical canvas size from the designer.
  // This ensures the panel renders at the exact same coordinate space as the
  // designer at 100% zoom, so component positions and sizes are consistent.
  const viewBox = useMemo(() => {
    const cfg = normalizedConfig.scadaConfig || {};
    const w = cfg.canvasWidth  || 1600;
    const h = cfg.canvasHeight || 900;
    return `0 0 ${w} ${h}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedConfig.scadaConfig]);

  // Render component — uses MemoizedIcon so SVG only re-renders when its colors change
  const renderComponent = (comp, svgData) => {
    const data = componentDataMap.get(comp.id);
    const bodyColor = data?.bodyColor || '#64748b';
    const isValveType = VALVE_TYPES.has(comp.type);

    if (svgData.type === 'component') {
      // svgData.width/height here are already comp.width/height (passed by the caller).
      // We render at natural (default) size and CSS-scale to the actual dimensions so
      // the SVG truly stretches to fill the bounding box on both axes.
      const natW = SVG_COMPONENTS[comp.type]?.width || svgData.width;
      const natH = SVG_COMPONENTS[comp.type]?.height || svgData.height;
      const scaleX = svgData.width / natW;
      const scaleY = svgData.height / natH;
      return (
        <foreignObject
          width={svgData.width}
          height={svgData.height}
          style={{ pointerEvents: 'none', overflow: 'hidden' }}
        >
          <div style={{ width: natW, height: natH, pointerEvents: 'none', transformOrigin: '0 0', transform: `scale(${scaleX}, ${scaleY})` }}>
            <MemoizedIcon
              Component={svgData.component}
              width={natW}
              height={natH}
              startColor={bodyColor}
              midColor={data?.midColor || lightenColor(bodyColor, 60)}
              endColor={data?.endColorDark || darkenColor(bodyColor, 20)}
              stemStartColor={isValveType ? data?.stemColor : undefined}
              stemMidColor={isValveType ? data?.stemMidColor : undefined}
              stemEndColor={isValveType ? data?.stemEndColor : undefined}
              fillPercent={(comp.type === 'controlValveHandActuator' || comp.type === 'controlValveDiaphragm') && comp.controlValveConfig?.op?.tagName ? (() => {
                const cfg = comp.controlValveConfig.op;
                const raw = parseFloat(tagValues?.[cfg.tagName]);
                if (isNaN(raw)) return 0;
                const min = cfg.min ?? 0;
                const max = cfg.max ?? 100;
                return Math.min(100, Math.max(0, ((raw - min) / (max - min || 1)) * 100));
              })() : undefined}
              fillColor={(comp.type === 'controlValveHandActuator' || comp.type === 'controlValveDiaphragm') ? (comp.controlValveConfig?.openColor || '#00ff00') : undefined}
            />
          </div>
        </foreignObject>
      );
    } else {
      // Legacy SVG with color overlay
      return (
        <g>
          <image
            href={svgData.svg}
            width={svgData.width}
            height={svgData.height}
          />
          <rect
            x={0}
            y={0}
            width={svgData.width}
            height={svgData.height}
            fill={bodyColor}
            opacity="0.6"
            style={{ mixBlendMode: 'multiply' }}
          />
        </g>
      );
    }
  };

  const canvasBg = normalizedConfig.scadaConfig?.backgroundColor;
  const bgImage = normalizedConfig.scadaConfig?.backgroundImage;
  const bgFit = normalizedConfig.scadaConfig?.backgroundFit || 'stretch';
  const bgOpacity = normalizedConfig.scadaConfig?.backgroundOpacity ?? 0.3;
  const bgTileSize = normalizedConfig.scadaConfig?.backgroundTileSize || 200;
  const canvasW = normalizedConfig.scadaConfig?.canvasWidth || 1600;
  const canvasH = normalizedConfig.scadaConfig?.canvasHeight || 900;

  return (
    <div ref={containerRef} style={{
      width: '100%',
      height: '100%',
      background: theme.bg,
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <svg
        width="100%"
        height="100%"
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block' }}
      >
        {/* Background Color — fills the canvas area */}
        {canvasBg && (
          <rect width={canvasW} height={canvasH} fill={canvasBg} style={{ pointerEvents: 'none' }} />
        )}

        {/* Background Image — inside SVG at canvas dimensions, same coordinate space as components.
            This ensures component positions on the background match the designer exactly. */}
        {bgImage && bgFit === 'tile' ? (
          <>
            <defs>
              <pattern id={`scada-bg-tile-${config.id}`} x="0" y="0" width={bgTileSize} height={bgTileSize} patternUnits="userSpaceOnUse">
                <image href={bgImage} x="0" y="0" width={bgTileSize} height={bgTileSize} preserveAspectRatio="xMidYMid meet" opacity={bgOpacity} />
              </pattern>
            </defs>
            <rect x={0} y={0} width={canvasW} height={canvasH} fill={`url(#scada-bg-tile-${config.id})`} style={{ pointerEvents: 'none' }} />
          </>
        ) : bgImage ? (
          <image
            href={bgImage}
            x={0} y={0}
            width={canvasW} height={canvasH}
            preserveAspectRatio={
              bgFit === 'contain' ? 'xMidYMid meet' :
              bgFit === 'cover' ? 'xMidYMid slice' :
              'none'
            }
            opacity={bgOpacity}
            style={{ pointerEvents: 'none' }}
          />
        ) : null}

        {/* Pipes/Lines */}
        {normalizedConfig.lines?.map(line => {
          const coords = getLineCoordinates(line);
          if (!coords) return null;

          // New-style pipe: has pipe-specific style or free-placement coordinates
          const isPipe = (line.style && ['single', 'double', 'insulated'].includes(line.style))
            || line.startX !== undefined;

          if (isPipe) {
            const { x1, y1, x2, y2 } = coords;
            const path = generatePipePath(x1, y1, x2, y2, line.controlPoints || []);
            // Resolve flow: tag overrides static flowEnabled setting
            const resolvedFlowEnabled = line.flowEnabled && (
              !line.tagName || String(tagValues[line.tagName]) === String(line.flowOnValue ?? '1')
            );
            return (
              <PipeElement
                key={line.id}
                id={line.id}
                path={path}
                width={line.width || 20}
                color={line.color || '#3b82f6'}
                style={['single','double','insulated'].includes(line.style) ? line.style : 'single'}
                flowEnabled={resolvedFlowEnabled}
                flowSpeed={line.flowSpeed || 1}
                flowDirection={line.flowDirection || 'forward'}
                flowColor={line.flowColor || '#60a5fa'}
                isSelected={false}
                darkMode={darkMode}
                onClick={line.displayTrend ? (e) => handleComponentClick(e, line, line.tagName || 'Pipe') : undefined}
              />
            );
          }

          // Legacy line rendering (fallback for very old diagrams)
          let pathD = `M ${coords.x1} ${coords.y1}`;
          if (line.controlPoints && line.controlPoints.length > 0) {
            line.controlPoints.forEach(cp => {
              pathD += ` L ${cp.x} ${cp.y}`;
            });
          }
          pathD += ` L ${coords.x2} ${coords.y2}`;

          return (
            <g key={line.id}>
              <path
                d={pathD}
                stroke={line.color || '#3b82f6'}
                strokeWidth={line.width || 2}
                fill="none"
                strokeDasharray={line.style === 'dashed' ? '5,5' : line.style === 'dotted' ? '2,3' : '0'}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          );
        })}

        {/* Components */}
        {normalizedConfig.components?.map(comp => {
          // Handle Navigation Button components
          if (comp.type === 'navButton') {
            const btnW = comp.width || 120;
            const btnH = comp.height || 40;
            const canNavigate = !!(onNavigate && comp.targetPanelId);
            const btnFill = comp.transparentBg ? 'transparent' : (comp.buttonColor || '#667eea');
            const btnTextColor = comp.textColor || '#ffffff';
            const btnFontSize = comp.fontSize || 13;
            const btnFontWeight = comp.fontWeight || '700';
            const btnTextAnchor = comp.textAlign === 'left' ? 'start' : comp.textAlign === 'right' ? 'end' : 'middle';
            const btnTextX = comp.textAlign === 'left' ? 8 : comp.textAlign === 'right' ? btnW - 8 : btnW / 2;
            return (
              <g key={comp.id} transform={`translate(${comp.x}, ${comp.y})`}>
                <rect x={0} y={0} width={btnW} height={btnH} fill={btnFill} rx={8} style={{ pointerEvents: 'none' }} />
                <text x={btnTextX} y={btnH / 2 + btnFontSize * 0.35} textAnchor={btnTextAnchor} fill={btnTextColor} fontSize={btnFontSize} fontWeight={btnFontWeight}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  {comp.label || 'Navigate'}
                </text>
                {/* Click target on top */}
                <rect x={0} y={0} width={btnW} height={btnH} fill="rgba(0,0,0,0)"
                  style={{ cursor: canNavigate ? 'pointer' : 'default' }}
                  onClick={(e) => { e.stopPropagation(); if (canNavigate) onNavigate(comp.targetPanelId); }}
                  onDoubleClick={(e) => handleDoubleClick(e, comp)}
                />
              </g>
            );
          }

          // Handle TextBox components
          if (comp.type === 'textbox') {
            return (
              <TextBoxElement
                key={comp.id}
                element={comp}
                tagValues={tagValues}
                darkMode={darkMode}
                isSelected={false}
                cursor={comp.displayTrend ? 'pointer' : 'default'}
                onClick={(e) => handleComponentClick(e, comp, comp.label || comp.textContent || 'Text')}
                onDoubleClick={(e) => handleDoubleClick(e, comp)}
              />
            );
          }

          // Handle Custom Image components
          if (comp.type === 'customImage') {
            const dim = getComponentDimensions(comp);
            const componentColor = componentDataMap.get(comp.id)?.bodyColor || getComponentColor(comp);

            return (
              <g
                key={comp.id}
                transform={`translate(${comp.x}, ${comp.y})`}
                style={{ cursor: comp.displayTrend ? 'pointer' : 'default' }}
                onClick={(e) => handleComponentClick(e, comp, comp.label || 'Image')}
                onDoubleClick={(e) => handleDoubleClick(e, comp)}
              >
                <g transform={`rotate(${comp.rotation || 0}, ${dim.width/2}, ${dim.height/2})`}>
                  <image
                    href={comp.imageData}
                    width={dim.width}
                    height={dim.height}
                  />
                  {comp.tagName && (
                    <rect
                      x={0}
                      y={0}
                      width={dim.width}
                      height={dim.height}
                      fill={componentColor}
                      opacity="0.4"
                      style={{ mixBlendMode: 'multiply' }}
                    />
                  )}
                </g>
                {/* Click overlay */}
                <rect x={0} y={0} width={dim.width} height={dim.height} fill="rgba(0,0,0,0)" style={{ pointerEvents: 'all', cursor: comp.displayTrend ? 'pointer' : 'default' }} />
                {/* Label */}
                {comp.label && (
                  <text
                    x={dim.width / 2}
                    y={dim.height + 16}
                    textAnchor="middle"
                    fill={theme.text}
                    fontSize="11"
                    fontWeight="600"
                  >
                    {comp.label}
                  </text>
                )}
              </g>
            );
          }

          // Handle drawLine components
          if (comp.type === 'drawLine') {
            const dim = getComponentDimensions(comp);
            const w = dim.width;
            const h = dim.height;
            const isH = comp.orientation !== 'vertical';
            const t = comp.lineThickness || 2;
            const dw = comp.dotWidth || 5;
            const dsh = comp.dashWidth || 10;
            const gw = comp.gapWidth || 5;
            let dashArray;
            if (comp.lineStyle === 'dotted') dashArray = `${dw} ${dw}`;
            else if (comp.lineStyle === 'dashed') dashArray = `${dsh} ${gw}`;
            else dashArray = undefined;
            const period = comp.lineStyle === 'dotted' ? dw * 2 : (dsh + gw);
            const animDur = comp.animEnabled && period > 0 && (comp.animSpeed || 0) > 0
              ? `${period / comp.animSpeed}s` : null;
            const x1 = isH ? 0     : w / 2;
            const y1 = isH ? h / 2 : 0;
            const x2 = isH ? w     : w / 2;
            const y2 = isH ? h / 2 : h;
            const lineColor = comp.highColor || '#000000';
            const sm = comp.startMarker || 'none';
            const em = comp.endMarker || 'none';
            const markerId = (type, end) => `marker-rt-${comp.id}-${end}-${type}`;
            const renderMarkerDef = (type, end) => {
              if (type === 'none') return null;
              const id = markerId(type, end);
              const sz = Math.max(t * 3, 8);
              if (type === 'arrow') {
                return <marker key={id} id={id} markerWidth={sz} markerHeight={sz} refX={end === 'end' ? sz - 1 : 1} refY={sz/2} orient="auto"><polygon points={end === 'end' ? `0,0 ${sz},${sz/2} 0,${sz}` : `${sz},0 0,${sz/2} ${sz},${sz}`} fill={lineColor}/></marker>;
              }
              if (type === 'circle') {
                return <marker key={id} id={id} markerWidth={sz} markerHeight={sz} refX={sz/2} refY={sz/2} orient="auto"><circle cx={sz/2} cy={sz/2} r={sz/2 - 1} fill="none" stroke={lineColor} strokeWidth="1.5"/></marker>;
              }
              if (type === 'diamond') {
                return <marker key={id} id={id} markerWidth={sz} markerHeight={sz} refX={sz/2} refY={sz/2} orient="auto"><polygon points={`${sz/2},0 ${sz},${sz/2} ${sz/2},${sz} 0,${sz/2}`} fill="none" stroke={lineColor} strokeWidth="1.5"/></marker>;
              }
              return null;
            };
            const compOpacity = comp.opacity ?? 1;
            return (
              <g key={comp.id} transform={`translate(${comp.x}, ${comp.y})`} opacity={compOpacity} style={{ pointerEvents: 'none' }}>
                <defs>
                  {renderMarkerDef(sm, 'start')}
                  {renderMarkerDef(em, 'end')}
                </defs>
                <rect x={0} y={0} width={w} height={h}
                  fill={comp.lowColor || '#141212'}
                  stroke={comp.borderWidth > 0 ? (comp.borderColor || '#000000') : 'none'}
                  strokeWidth={comp.borderWidth || 0}
                />
                <line x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={lineColor}
                  strokeWidth={t}
                  strokeDasharray={dashArray}
                  strokeLinecap="round"
                  markerStart={sm !== 'none' ? `url(#${markerId(sm, 'start')})` : undefined}
                  markerEnd={em !== 'none' ? `url(#${markerId(em, 'end')})` : undefined}
                >
                  {animDur && dashArray && (
                    <animate attributeName="stroke-dashoffset"
                      from="0" to={`-${period}`}
                      dur={animDur} repeatCount="indefinite" />
                  )}
                </line>
              </g>
            );
          }

          // Handle shape components
          if (comp.type === 'shape') {
            const dim = getComponentDimensions(comp);
            const w = dim.width;
            const h = dim.height;
            const solidFill = comp.fillColor || '#272E35';
            const stroke = comp.enableBorder ? (comp.borderColor || '#000000') : 'none';
            const sw = comp.enableBorder ? (comp.borderWidth || 2) : 0;
            const cr = comp.cornerRadius || 0;
            const st = comp.shapeType || 'rectangle';
            const ft = comp.fillType || 'solid';
            const compOpacity = comp.opacity ?? 1;
            const gradId = `grad-rt-${comp.id}`;
            let fill = solidFill;
            let gradientDef = null;
            if (ft === 'linear') {
              const angle = (comp.gradientAngle ?? 180) * Math.PI / 180;
              const x1g = 50 - 50 * Math.cos(angle); const y1g = 50 - 50 * Math.sin(angle);
              const x2g = 50 + 50 * Math.cos(angle); const y2g = 50 + 50 * Math.sin(angle);
              gradientDef = <linearGradient id={gradId} x1={`${x1g}%`} y1={`${y1g}%`} x2={`${x2g}%`} y2={`${y2g}%`}><stop offset="0%" stopColor={solidFill}/><stop offset="100%" stopColor={comp.gradientColor2 || '#667eea'}/></linearGradient>;
              fill = `url(#${gradId})`;
            } else if (ft === 'radial') {
              gradientDef = <radialGradient id={gradId} cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor={solidFill}/><stop offset="100%" stopColor={comp.gradientColor2 || '#667eea'}/></radialGradient>;
              fill = `url(#${gradId})`;
            }
            let shapeEl;
            if (st === 'circle' || st === 'ellipse') {
              shapeEl = <ellipse cx={w/2} cy={h/2} rx={w/2} ry={h/2} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'triangle') {
              shapeEl = <polygon points={`${w/2},0 ${w},${h} 0,${h}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'diamond') {
              shapeEl = <polygon points={`${w/2},0 ${w},${h/2} ${w/2},${h} 0,${h/2}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'pentagon') {
              const pts = Array.from({length:5},(_,i)=>{const a=Math.PI*2*i/5-Math.PI/2;return`${w/2+w/2*Math.cos(a)},${h/2+h/2*Math.sin(a)}`;}).join(' ');
              shapeEl = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'hexagon') {
              const pts = Array.from({length:6},(_,i)=>{const a=Math.PI*2*i/6-Math.PI/6;return`${w/2+w/2*Math.cos(a)},${h/2+h/2*Math.sin(a)}`;}).join(' ');
              shapeEl = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'star') {
              const pts = Array.from({length:10},(_,i)=>{const a=Math.PI*2*i/10-Math.PI/2;const r2=i%2===0?0.5:0.2;return`${w/2+w*r2*Math.cos(a)},${h/2+h*r2*Math.sin(a)}`;}).join(' ');
              shapeEl = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'arrow') {
              shapeEl = <polygon points={`0,${h*0.3} ${w*0.6},${h*0.3} ${w*0.6},0 ${w},${h/2} ${w*0.6},${h} ${w*0.6},${h*0.7} 0,${h*0.7}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'cross') {
              const t = 0.3;
              shapeEl = <polygon points={`${w*t},0 ${w*(1-t)},0 ${w*(1-t)},${h*t} ${w},${h*t} ${w},${h*(1-t)} ${w*(1-t)},${h*(1-t)} ${w*(1-t)},${h} ${w*t},${h} ${w*t},${h*(1-t)} 0,${h*(1-t)} 0,${h*t} ${w*t},${h*t}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'parallelogram') {
              const sk = w * 0.2;
              shapeEl = <polygon points={`${sk},0 ${w},0 ${w-sk},${h} 0,${h}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'trapezoid') {
              const sk = w * 0.15;
              shapeEl = <polygon points={`${sk},0 ${w-sk},0 ${w},${h} 0,${h}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else if (st === 'roundedRect') {
              shapeEl = <rect x={0} y={0} width={w} height={h} rx={cr || 12} ry={cr || 12} fill={fill} stroke={stroke} strokeWidth={sw} />;
            } else {
              shapeEl = <rect x={0} y={0} width={w} height={h} rx={cr} ry={cr} fill={fill} stroke={stroke} strokeWidth={sw} />;
            }
            return (
              <g key={comp.id} transform={`translate(${comp.x}, ${comp.y})`} opacity={compOpacity} style={{ pointerEvents: 'none' }}>
                {gradientDef && <defs>{gradientDef}</defs>}
                {shapeEl}
              </g>
            );
          }

          // Handle LED components
          if (comp.type === 'led') {
            const dim = getComponentDimensions(comp);
            const ledData = componentDataMap.get(comp.id);
            const componentColor = ledData?.bodyColor || getComponentColor(comp);
            const status = ledData?.status !== undefined ? ledData.status : getComponentStatus(comp);
            const ledSize = Math.min(dim.width, dim.height);
            const cx = ledSize / 2;
            const cy = ledSize / 2;
            const r = ledSize / 2 - 2;
            return (
              <g key={comp.id} transform={`translate(${comp.x}, ${comp.y})`}
                style={{ cursor: comp.displayTrend ? 'pointer' : 'default' }}
                onClick={(e) => handleComponentClick(e, comp, comp.label || 'LED')}
                onDoubleClick={(e) => handleDoubleClick(e, comp)}>
                <circle cx={cx} cy={cy} r={r}
                  fill={componentColor}
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth={2}
                />
                <circle cx={cx - r * 0.25} cy={cy - r * 0.25} r={r * 0.3}
                  fill="rgba(255,255,255,0.3)"
                />
                {comp.label && (
                  <text x={cx} y={ledSize + 16}
                    textAnchor="middle" fill={theme.text}
                    fontSize="11" fontWeight="600"
                  >
                    {comp.label}
                  </text>
                )}
                {comp.showStatusLabel !== false && status && (
                  <g>
                    <rect x={cx - 28} y={ledSize + 22}
                      width="56" height="16"
                      fill={componentColor} rx="3" opacity="0.95"
                    />
                    <text x={cx} y={ledSize + 33}
                      textAnchor="middle" fill="white"
                      fontSize="9" fontWeight="700"
                    >
                      {status}
                    </text>
                  </g>
                )}
              </g>
            );
          }

          // Handle standard SVG components
          const svgData = SVG_COMPONENTS[comp.type];
          if (!svgData) return null;

          const dim = getComponentDimensions(comp);
          const data = componentDataMap.get(comp.id);
          const componentColor = data?.bodyColor || getComponentColor(comp);
          const status = data?.status !== undefined ? data.status : getComponentStatus(comp);

          const isPump = PUMP_TYPES.has(comp.type);
          const isValve = VALVE_TYPES.has(comp.type);
          const isControlValve = CONTROL_VALVE_TYPES.has(comp.type);
          return (
            <g
              key={comp.id}
              transform={`translate(${comp.x}, ${comp.y})`}
              style={{ cursor: ((isPump || isValve || isControlValve) && comp.showFaceplate) || comp.displayTrend ? 'pointer' : 'default' }}
              onClick={(e) => {
                if (isPump) { handlePumpClick(e, comp); }
                else if (isValve) { handleValveClick(e, comp); }
                else if (isControlValve) { handleControlValveClick(e, comp); }
                else { handleComponentClick(e, comp, comp.label || svgData?.name || 'Component'); }
              }}
              onDoubleClick={(e) => handleDoubleClick(e, comp)}
            >
              <g transform={`rotate(${comp.rotation || 0}, ${dim.width/2}, ${dim.height/2})`}>
                {renderComponent(comp, { ...svgData, width: dim.width, height: dim.height })}
              </g>

              {/* Click overlay */}
              <rect x={0} y={0} width={dim.width} height={dim.height} fill="rgba(0,0,0,0)" style={{ pointerEvents: 'all', cursor: comp.displayTrend ? 'pointer' : 'default' }} />

              {/* Label */}
              <text
                x={dim.width / 2}
                y={dim.height + 18}
                textAnchor="middle"
                fill={theme.text}
                fontSize="11"
                fontWeight="600"
              >
                {comp.label}
              </text>

              {/* Status indicator */}
              {comp.showStatusLabel !== false && status && (
                <g>
                  <rect
                    x={dim.width / 2 - 30}
                    y={dim.height + 24}
                    width="60"
                    height="18"
                    fill={componentColor}
                    rx="4"
                    opacity="0.9"
                  />
                  <text
                    x={dim.width / 2}
                    y={dim.height + 36}
                    textAnchor="middle"
                    fill="white"
                    fontSize="10"
                    fontWeight="700"
                  >
                    {status}
                  </text>
                </g>
              )}

              {/* Value display */}
              {comp.showTagValue !== false && comp.tagName && tagValues[comp.tagName] && (
                <text
                  x={dim.width / 2}
                  y={dim.height + 50}
                  textAnchor="middle"
                  fill={theme.textSecondary}
                  fontSize="9"
                  fontWeight="500"
                >
                  {comp.tagName}: {tagValues[comp.tagName]}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {isActive && chartPopups.map(popup => (
        <TagChartPopup
          key={popup.id}
          tags={popup.tags}
          position={popup.position}
          componentLabel={popup.label}
          onClose={() => setChartPopups(prev => prev.filter(p => p.id !== popup.id))}
          darkMode={darkMode}
          plantId={plantId}
        />
      ))}

      {/* Pump, Valve & Control Valve Faceplates — one per open component, only when this panel is active */}
      {isActive && openFaceplates.map(comp => {
        const svgData = SVG_COMPONENTS[comp.type];
        if (CONTROL_VALVE_TYPES.has(comp.type)) {
          return (
            <ControlValveFaceplate
              key={comp.id}
              comp={comp}
              tagValues={tagValues}
              ValveIconComponent={svgData?.component || null}
              onClose={() => setOpenFaceplates(prev => prev.filter(f => f.id !== comp.id))}
              darkMode={darkMode}
              plantId={plantId}
            />
          );
        }
        if (VALVE_TYPES.has(comp.type)) {
          return (
            <ValveFaceplate
              key={comp.id}
              comp={comp}
              tagValues={tagValues}
              ValveIconComponent={svgData?.component || null}
              onClose={() => setOpenFaceplates(prev => prev.filter(f => f.id !== comp.id))}
              darkMode={darkMode}
              plantId={plantId}
            />
          );
        }
        return (
          <PumpFaceplate
            key={comp.id}
            comp={comp}
            tagValues={tagValues}
            PumpIconComponent={svgData?.component || null}
            onClose={() => setOpenFaceplates(prev => prev.filter(f => f.id !== comp.id))}
            darkMode={darkMode}
            plantId={plantId}
          />
        );
      })}

      {/* Tag Inspectors — double-click on any component */}
      {isActive && tagInspectors.map(ti => (
        <TagInspectorPopup
          key={ti.id}
          comp={ti.comp}
          allTags={ti.allTags}
          tagValues={tagValues}
          position={ti.position}
          onClose={() => setTagInspectors(prev => prev.filter(t => t.id !== ti.id))}
          darkMode={darkMode}
          plantId={plantId}
        />
      ))}
    </div>
  );
}

export default React.memo(ScadaPanel);
